import { firestore } from "../firebase";
import DB_PATH from "../DB_PATH";
import { Plan } from "../../types/Plan";
import { DayPlan } from "../../types/DayPlan";
import { DailyStep, DAILY_STEP_TYPE } from "../../types/DailyStep";
import { Action, ActionType, Subaction } from "../../types/ActionType";
import { Block } from "../../types/Block";
import {
  WorkoutProgramDraft,
  WorkoutPhaseDraft,
  WorkoutSessionDraft,
  WorkoutBlockDraft,
  WorkoutSubactionDraft,
} from "../../components/screens/plan/components/workoutProgramApi";

export type CreateWorkoutProgramProgressEvent =
  | { stage: "library_dedup"; completed: number; total: number }
  | { stage: "library_done"; created: number; reused: number }
  | {
      stage: "building";
      actionCount: number;
      dayPlanCount: number;
      stepCount: number;
    }
  | { stage: "committing"; totalDocs: number }
  | { stage: "done"; totalDocs: number };

export type CreateWorkoutProgramParams = {
  draft: WorkoutProgramDraft;
  communityId: string;
  authorId: string;
  authorName: string;
  libraryHabitId: string;
  categoryId?: string;
  coverImageURL?: string;
  subcategoryId?: string;
  subcategoryName?: string;
  onProgress?: (event: CreateWorkoutProgramProgressEvent) => void;
};

export type CreateWorkoutProgramResult = {
  planId: string;
  actionIds: string[];
  libraryExerciseCount: number;
  reusedExerciseCount: number;
};

export async function createWorkoutProgram(
  params: CreateWorkoutProgramParams
): Promise<CreateWorkoutProgramResult> {
  const {
    draft,
    communityId,
    authorId,
    authorName,
    libraryHabitId,
    categoryId,
    coverImageURL,
    subcategoryId,
    subcategoryName,
    onProgress,
  } = params;
  const createdAt = new Date().toISOString();

  // Step 1: library Subactions dedup + create (sequential — needs reads for dedup)
  const dedupResult = await dedupAndCreateLibrarySubactions(
    draft,
    communityId,
    libraryHabitId,
    createdAt,
    onProgress
  );
  const { libraryIdByName, createdCount, reusedCount } = dedupResult;
  onProgress?.({ stage: "library_done", created: createdCount, reused: reusedCount });

  // Step 2: build everything in memory with pre-generated IDs, accumulate into a batch
  const batch = firestore.batch();

  // Plan ref
  const planRef = firestore.collection(DB_PATH.PLAN).doc();
  const planId = planRef.id;

  // Action refs (one per phase × session)
  const actionIdByKey = new Map<string, string>();
  const actionIds: string[] = [];
  for (const phase of draft.phases) {
    for (const session of phase.sessions) {
      const actionRef = firestore.collection(DB_PATH.HABIT_ACTION).doc();
      const actionId = actionRef.id;
      const action = buildActionData({
        session,
        phase,
        actionId,
        communityId,
        libraryHabitId,
        libraryIdByName,
        createdAt,
        warmupProtocol: draft.warmupProtocol,
        planId,
      });
      batch.set(actionRef, stripUndefined(action));
      actionIdByKey.set(sessionKey(phase.phaseNumber, session.position), actionId);
      actionIds.push(actionId);
    }
  }

  // Plan doc
  const plan = buildPlanData({
    draft,
    planId,
    communityId,
    authorId,
    authorName,
    createdAt,
    categoryId,
    coverImageURL,
    subcategoryId,
    subcategoryName,
  });
  batch.set(planRef, stripUndefined(plan));

  // DayPlan and DailyStep docs (one week at a time)
  let dayPlanCount = 0;
  let stepCount = 0;
  for (const phase of draft.phases) {
    const [startWeek, endWeek] = phase.weekRange;
    for (let weekNumber = startWeek; weekNumber <= endWeek; weekNumber++) {
      const dayPlanRef = firestore.collection(DB_PATH.PLAN_DAY).doc();
      const dayPlanId = dayPlanRef.id;
      const dayPlan = buildDayPlanData({
        weekNumber,
        phase,
        dayPlanId,
        planId,
        communityId,
        authorId,
        createdAt,
      });
      batch.set(dayPlanRef, stripUndefined(dayPlan));
      dayPlanCount += 1;

      for (const session of phase.sessions) {
        const actionId = actionIdByKey.get(
          sessionKey(phase.phaseNumber, session.position)
        );
        if (!actionId) continue;

        const stepRef = firestore.collection(DB_PATH.PLAN_STEP).doc();
        const step = buildStepData({
          session,
          phase,
          stepId: stepRef.id,
          dayPlanId,
          planId,
          actionId,
          communityId,
          authorId,
          authorName,
        });
        batch.set(stepRef, stripUndefined(step));
        stepCount += 1;
      }
    }
  }

  const totalDocs = 1 + actionIds.length + dayPlanCount + stepCount;
  onProgress?.({
    stage: "building",
    actionCount: actionIds.length,
    dayPlanCount,
    stepCount,
  });
  onProgress?.({ stage: "committing", totalDocs });

  // Step 3: commit atomically — either all docs write or none do
  await batch.commit();
  onProgress?.({ stage: "done", totalDocs });

  return {
    planId,
    actionIds,
    libraryExerciseCount: createdCount,
    reusedExerciseCount: reusedCount,
  };
}

// ─── Library Subaction dedup (sequential, idempotent) ─────────────────────

type DedupResult = {
  libraryIdByName: Map<string, string>;
  createdCount: number;
  reusedCount: number;
};

async function dedupAndCreateLibrarySubactions(
  draft: WorkoutProgramDraft,
  communityId: string,
  libraryHabitId: string,
  createdAt: string,
  onProgress?: (event: CreateWorkoutProgramProgressEvent) => void
): Promise<DedupResult> {
  const uniqueNames = collectUniqueExerciseNames(draft);

  const subactionCollection = firestore.collection(DB_PATH.HABIT_SUBACTION);
  const libraryIdByName = new Map<string, string>();
  let createdCount = 0;
  let reusedCount = 0;

  onProgress?.({ stage: "library_dedup", completed: 0, total: uniqueNames.length });

  for (let i = 0; i < uniqueNames.length; i++) {
    const name = uniqueNames[i];
    const existingSnapshot = await subactionCollection
      .where("habitId", "==", libraryHabitId)
      .where("name", "==", name)
      .limit(1)
      .get();

    if (!existingSnapshot.empty) {
      libraryIdByName.set(name, existingSnapshot.docs[0].id);
      reusedCount += 1;
    } else {
      const newRef = subactionCollection.doc();
      const newId = newRef.id;
      const librarySubaction: Subaction = {
        id: newId,
        name,
        description: "",
        habitId: libraryHabitId,
        actionType: ActionType.EXERCISE,
        createdAt,
        communityId,
      };
      await newRef.set(stripUndefined(librarySubaction));
      libraryIdByName.set(name, newId);
      createdCount += 1;
    }

    onProgress?.({
      stage: "library_dedup",
      completed: i + 1,
      total: uniqueNames.length,
    });
  }

  return { libraryIdByName, createdCount, reusedCount };
}

function collectUniqueExerciseNames(draft: WorkoutProgramDraft): string[] {
  const names = new Set<string>();
  for (const phase of draft.phases) {
    for (const session of phase.sessions) {
      for (const block of session.blocks) {
        for (const sub of block.subactions) {
          if (sub.name?.trim()) names.add(sub.name.trim());
          for (const alt of sub.alternatives ?? []) {
            if (alt.name?.trim()) names.add(alt.name.trim());
          }
        }
      }
    }
  }
  return Array.from(names);
}

// ─── Pure builders (return doc data, no writes) ────────────────────────────

function buildActionData(args: {
  session: WorkoutSessionDraft;
  phase: WorkoutPhaseDraft;
  actionId: string;
  communityId: string;
  libraryHabitId: string;
  libraryIdByName: Map<string, string>;
  createdAt: string;
  warmupProtocol?: WorkoutProgramDraft["warmupProtocol"];
  planId: string;
}): Action {
  const {
    session,
    phase,
    actionId,
    communityId,
    libraryHabitId,
    libraryIdByName,
    createdAt,
    warmupProtocol,
    planId,
  } = args;

  const blocks: Block[] = session.blocks.map((block) =>
    buildBlockData(block, libraryIdByName, libraryHabitId, communityId, createdAt, actionId)
  );

  return {
    id: actionId,
    name: `${phase.name} - ${session.title}`,
    description: session.description,
    habitId: libraryHabitId,
    actionType: ActionType.EXERCISE,
    blocks,
    warmupProtocol,
    restDayAfter: session.restDayAfter,
    createdAt,
    planID: planId,
  };
}

function buildBlockData(
  draft: WorkoutBlockDraft,
  libraryIdByName: Map<string, string>,
  libraryHabitId: string,
  communityId: string,
  createdAt: string,
  actionId: string
): Block {
  return {
    type: draft.type,
    label: draft.label,
    position: draft.position,
    subactions: draft.subactions.map((sub) =>
      buildEmbeddedSubaction(
        sub,
        libraryIdByName,
        libraryHabitId,
        communityId,
        createdAt,
        actionId
      )
    ),
  };
}

function buildEmbeddedSubaction(
  draft: WorkoutSubactionDraft,
  libraryIdByName: Map<string, string>,
  libraryHabitId: string,
  communityId: string,
  createdAt: string,
  actionId: string
): Subaction {
  const libraryId = libraryIdByName.get(draft.name.trim());
  const alternatives = (draft.alternatives ?? []).map((alt) => ({
    name: alt.name,
    subactionId: libraryIdByName.get(alt.name.trim()),
  }));

  return {
    id: firestore.collection(DB_PATH.HABIT_SUBACTION).doc().id,
    subactionId: libraryId,
    name: draft.name,
    description: "",
    habitId: libraryHabitId,
    actionId,
    actionType: ActionType.EXERCISE,
    createdAt,
    communityId,
    notes: draft.notes,
    prescription: draft.prescription,
    alternatives,
  };
}

function buildPlanData(args: {
  draft: WorkoutProgramDraft;
  planId: string;
  communityId: string;
  authorId: string;
  authorName: string;
  createdAt: string;
  categoryId?: string;
  coverImageURL?: string;
  subcategoryId?: string;
  subcategoryName?: string;
}): Plan {
  const {
    draft,
    planId,
    communityId,
    authorId,
    authorName,
    createdAt,
    categoryId,
    coverImageURL,
    subcategoryId,
    subcategoryName,
  } = args;
  return {
    id: planId,
    title: draft.title,
    shortDescription: draft.shortDescription,
    description: draft.description,
    coverImageURL: coverImageURL ?? "",
    createdAt,
    categoryId: categoryId ?? "workout",
    categoryName: "Workout",
    subcategoryId,
    subcategoryName,
    authorId,
    authorName,
    communityId,
    isActive: true,
    level: undefined as any,
    tags: draft.tags,
    planType: "exercise",
    phases: draft.phases.map(stripSessionsFromPhase),
  };
}

function stripSessionsFromPhase(phase: WorkoutPhaseDraft) {
  return {
    phaseNumber: phase.phaseNumber,
    name: phase.name,
    description: phase.description,
    weekRange: phase.weekRange,
    coachNote: phase.coachNote,
  };
}

function buildDayPlanData(args: {
  weekNumber: number;
  phase: WorkoutPhaseDraft;
  dayPlanId: string;
  planId: string;
  communityId: string;
  authorId: string;
  createdAt: string;
}): DayPlan {
  const { weekNumber, phase, dayPlanId, planId, communityId, authorId, createdAt } = args;
  return {
    id: dayPlanId,
    createdAt,
    title: `Week ${weekNumber}`,
    description: phase.description,
    steps: [],
    planID: planId,
    position: weekNumber,
    communityId,
    dayPlanType: "workout",
    phaseNumber: phase.phaseNumber,
    authorID: authorId,
  };
}

function buildStepData(args: {
  session: WorkoutSessionDraft;
  phase: WorkoutPhaseDraft;
  stepId: string;
  dayPlanId: string;
  planId: string;
  actionId: string;
  communityId: string;
  authorId: string;
  authorName: string;
}): DailyStep {
  const { session, phase, stepId, dayPlanId, planId, actionId, communityId, authorId, authorName } = args;
  return {
    id: stepId,
    title: session.title,
    subtitle: session.description,
    sectionTitle: phase.name,
    description: session.description,
    quote: "",
    backgroundImageURL: "",
    reference: "",
    type: DAILY_STEP_TYPE.WORKOUT,
    dayPlanID: dayPlanId,
    position: session.position,
    planID: planId,
    authorID: authorId,
    authorName,
    communityId,
    workoutActionId: actionId,
  };
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function sessionKey(phaseNumber: number, sessionPosition: number): string {
  return `${phaseNumber}_${sessionPosition}`;
}

function stripUndefined<T extends Record<string, any>>(value: T): T {
  return stripUndefinedDeep(value) as T;
}

function stripUndefinedDeep(value: any): any {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) {
    return value
      .map(stripUndefinedDeep)
      .filter((item) => item !== undefined);
  }
  if (typeof value === "object") {
    const result: Record<string, any> = {};
    for (const key of Object.keys(value)) {
      const v = stripUndefinedDeep(value[key]);
      if (v === undefined) continue;
      result[key] = v;
    }
    return result;
  }
  return value;
}
